import React, { useState, useEffect, useCallback } from 'react';
import './VLLMServerPanel.css';

const VLLMServerPanel = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? `http://${window.location.hostname}:3002` : '');

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // 'start' | 'stop' | null
  const [alert, setAlert] = useState(null); // { type: 'success'|'error'|'info', message }
  const [config, setConfig] = useState({
    vllmEndpoint: '',
    modelName: '',
    runpodApiKey: '',
    runpodPodId: '',
    hfRepo: '',
    provider: 'tensordock',
    tensorDockIp: '',
    tensorDockSshPort: '',
    tensorDockApiPort: '',
  });
  const [configDirty, setConfigDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [reconnectOpen, setReconnectOpen] = useState(false);
  const [reconnectForm, setReconnectForm] = useState({ ip: '', sshPort: '', apiPort: '', gpuCount: 1 });
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectChecklist, setReconnectChecklist] = useState(null);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    if (type !== 'error') {
      setTimeout(() => setAlert(null), 5000);
    }
  };

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/vllm/status`, { cache: 'no-cache' });
      const data = await res.json();
      if (data.success) {
        setStatus(data);
        if (data.config) {
          setConfig(prev => ({ ...prev, ...data.config }));
        }
      }
    } catch (e) {
      setStatus({ vllm: { online: false, error: e.message }, pod: { status: 'unknown' } });
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handlePodAction = async (action) => {
    setActionLoading(action);
    setAlert(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/vllm/pod/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.success) {
        showAlert('success', data.message);
        // 3초 후 상태 새로고침
        setTimeout(() => fetchStatus(), 3000);
      } else {
        showAlert('error', data.error || '요청 실패');
      }
    } catch (e) {
      showAlert('error', e.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleTdAction = async (action) => {
    setActionLoading(`td-${action}`);
    setAlert(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/vllm/td/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.success) {
        showAlert('success', data.message);
        if (action === 'restart') {
          showAlert('info', 'vLLM 서버 시작 중... 약 1~2분 후 상태를 확인하세요.');
          setTimeout(() => fetchStatus(), 60000);
        } else {
          setTimeout(() => fetchStatus(), 3000);
        }
      } else {
        showAlert('error', data.error || '요청 실패');
      }
    } catch (e) {
      showAlert('error', `SSH 연결 실패: ${e.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    setConfigDirty(true);
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/vllm/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      const data = await res.json();
      if (data.success) {
        showAlert('success', '설정이 저장되었습니다.');
        setConfigDirty(false);
        fetchStatus();
      } else {
        showAlert('error', data.error || '저장 실패');
      }
    } catch (e) {
      showAlert('error', e.message);
    } finally {
      setSaving(false);
    }
  };

  const getVllmStatusDot = () => {
    if (!status) return 'unknown';
    return status.vllm?.online ? 'online' : 'offline';
  };

  const getPodStatusDot = () => {
    if (!status?.pod) return 'unknown';
    const s = status.pod.status;
    if (s === 'RUNNING') return 'online';
    if (s === 'STARTING' || s === 'RESUMING') return 'starting';
    if (s === 'EXITED' || s === 'STOPPED') return 'offline';
    return 'unknown';
  };

  const getPodStatusLabel = () => {
    if (!status?.pod) return '확인 중...';
    const map = {
      'RUNNING': '실행 중',
      'STARTING': '시작 중...',
      'RESUMING': '재개 중...',
      'EXITED': '중지됨',
      'STOPPED': '중지됨',
    };
    return map[status.pod.status] || status.pod.status || '알 수 없음';
  };

  const formatUptime = (seconds) => {
    if (!seconds) return '-';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}시간 ${m}분` : `${m}분`;
  };

  return (
    <div className="vllm-panel">
      <div className="vllm-header">
        <h2>🖥️ vLLM 서버 관리</h2>
        <div className="vllm-header-actions">
          <button
            className="btn-refresh-status"
            onClick={fetchStatus}
            disabled={loading}
          >
            {loading ? <><span className="vllm-spinner" /> 확인 중...</> : '🔄 상태 새로고침'}
          </button>
        </div>
      </div>

      {/* 알림 */}
      {alert && (
        <div className={`vllm-alert ${alert.type}`}>
          {alert.type === 'success' ? '✅' : alert.type === 'error' ? '❌' : 'ℹ️'} {alert.message}
        </div>
      )}

      {/* 상태 카드 */}
      <div className="vllm-status-grid">
        {/* vLLM 서버 상태 */}
        <div className="vllm-status-card">
          <h3><span className={`status-dot ${getVllmStatusDot()}`} /> vLLM 서버</h3>
          <div className={`status-label ${getVllmStatusDot()}`}>
            {!status ? '확인 중...' : status.vllm?.online ? '온라인' : '오프라인'}
          </div>
          {status?.vllm?.online && status.vllm.models?.length > 0 && (
            <div className="status-detail"><strong>모델:</strong> {status.vllm.models.join(', ')}</div>
          )}
          {status?.vllm?.error && (
            <div className="status-detail" style={{ color: '#dc3545' }}>{status.vllm.error}</div>
          )}
          <div className="status-detail"><strong>엔드포인트:</strong> {config.vllmEndpoint || '-'}</div>
        </div>

        {/* GPU 서버 상태 */}
        <div className="vllm-status-card">
          <h3><span className={`status-dot ${getVllmStatusDot()}`} /> GPU 서버</h3>
          <div className={`status-label ${getVllmStatusDot()}`}>
            {!status ? '확인 중...' : status.vllm?.online ? '연결됨' : '연결 안됨'}
          </div>
          <div className="status-detail"><strong>제공자:</strong> {{
            tensordock: 'TensorDock',
            runpod: 'RunPod',
            lambda: 'Lambda',
            vastai: 'Vast.ai',
            custom: '직접 서버'
          }[config.provider] || config.provider}</div>
          {config.provider === 'tensordock' && (
            <>
              <div className="status-detail"><strong>서버:</strong> {config.tensorDockIp || config.vllmEndpoint?.replace('http://', '').split(':')[0] || '-'}</div>
              <div className="status-detail"><strong>GPU:</strong> A100 80GB</div>
              {config.tensorDockIp && (
                <div className="vllm-pod-actions">
                  <button
                    className="btn-pod start"
                    onClick={() => handleTdAction('restart')}
                    disabled={!!actionLoading}
                  >
                    {actionLoading === 'td-restart' ? <><span className="vllm-spinner" /> 재시작 중...</> : '🔄 vLLM 재시작'}
                  </button>
                  <button
                    className="btn-pod stop"
                    onClick={() => handleTdAction('stop')}
                    disabled={!!actionLoading || getVllmStatusDot() === 'offline'}
                  >
                    {actionLoading === 'td-stop' ? <><span className="vllm-spinner" /> 중지 중...</> : '⏹ vLLM 중지'}
                  </button>
                </div>
              )}
            </>
          )}
          {config.provider === 'runpod' && (
            <>
              {status?.pod?.name && (
                <div className="status-detail"><strong>Pod:</strong> {status.pod.name}</div>
              )}
              {status?.pod?.gpu && (
                <div className="status-detail"><strong>GPU:</strong> {status.pod.gpu}</div>
              )}
              {status?.pod?.costPerHr && (
                <div className="status-detail"><strong>비용:</strong> ${status.pod.costPerHr}/hr</div>
              )}
              {config.runpodApiKey && (
                <div className="vllm-pod-actions">
                  <button
                    className="btn-pod start"
                    onClick={() => handlePodAction('start')}
                    disabled={actionLoading || getPodStatusDot() === 'online' || getPodStatusDot() === 'starting'}
                  >
                    {actionLoading === 'start' ? <><span className="vllm-spinner" /> 시작 중...</> : '▶ Pod 시작'}
                  </button>
                  <button
                    className="btn-pod stop"
                    onClick={() => handlePodAction('stop')}
                    disabled={actionLoading || getPodStatusDot() === 'offline'}
                  >
                    {actionLoading === 'stop' ? <><span className="vllm-spinner" /> 중지 중...</> : '⏹ Pod 중지'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* TensorDock 재연결 위젯 */}
      {config.provider === 'tensordock' && (
        <div className="vllm-reconnect-section">
          <button
            className="vllm-reconnect-toggle"
            onClick={() => setReconnectOpen(!reconnectOpen)}
          >
            {reconnectOpen ? '▼' : '▶'} 🔌 VM 재시작 후 재연결
            {getVllmStatusDot() === 'offline' && <span style={{color:'#dc3545', marginLeft: 8, fontSize: '0.85em'}}>● 오프라인 — 재연결 필요</span>}
          </button>

          {reconnectOpen && (
            <div className="vllm-reconnect-content">
              <p style={{color:'#666', margin:'0 0 12px', fontSize:'0.9em'}}>
                TensorDock VM 재시작 후 대시보드에서 새 IP/포트를 확인하고 입력하세요.
                자동으로 설정 업데이트 → SSH 검증 → vLLM 시작 → 헬스체크를 수행합니다.
              </p>
              <div className="reconnect-form">
                <div className="reconnect-field">
                  <label>서버 IP</label>
                  <input
                    className="mono"
                    value={reconnectForm.ip}
                    onChange={(e) => setReconnectForm(p => ({...p, ip: e.target.value}))}
                    placeholder="80.188.223.202"
                  />
                </div>
                <div className="reconnect-field">
                  <label>SSH 포트</label>
                  <input
                    className="mono"
                    value={reconnectForm.sshPort}
                    onChange={(e) => setReconnectForm(p => ({...p, sshPort: e.target.value}))}
                    placeholder="10239"
                  />
                </div>
                <div className="reconnect-field">
                  <label>API 포트</label>
                  <input
                    className="mono"
                    value={reconnectForm.apiPort}
                    onChange={(e) => setReconnectForm(p => ({...p, apiPort: e.target.value}))}
                    placeholder="10238"
                  />
                </div>
                <div className="reconnect-field">
                  <label>GPU 수</label>
                  <select
                    value={reconnectForm.gpuCount}
                    onChange={(e) => setReconnectForm(p => ({...p, gpuCount: parseInt(e.target.value)}))}
                  >
                    <option value={1}>1개 (A100 80GB)</option>
                    <option value={2}>2개 (TP=2)</option>
                    <option value={4}>4개 (TP=4)</option>
                  </select>
                </div>
              </div>

              <button
                className="btn-reconnect"
                disabled={reconnecting || !reconnectForm.ip || !reconnectForm.sshPort || !reconnectForm.apiPort}
                onClick={async () => {
                  setReconnecting(true);
                  setReconnectChecklist(null);
                  try {
                    const res = await fetch(`${API_BASE_URL}/api/vllm/td/reconnect`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(reconnectForm),
                    });
                    const data = await res.json();
                    setReconnectChecklist(data.checklist);
                    if (data.success) {
                      showAlert('success', '✅ 재연결 완료! 모든 설정이 업데이트되었습니다.');
                      fetchStatus();
                    } else if (data.checklist?.healthCheck?.status === 'wait') {
                      showAlert('info', '⏳ vLLM 모델 로딩 중... 1~2분 후 상태 새로고침하세요.');
                    } else {
                      showAlert('error', data.error || '재연결 실패');
                    }
                  } catch (e) {
                    showAlert('error', `재연결 실패: ${e.message}`);
                  } finally {
                    setReconnecting(false);
                  }
                }}
              >
                {reconnecting ? <><span className="vllm-spinner" /> 재연결 중... (최대 2분)</> : '🔌 재연결 시작'}
              </button>

              {/* 체크리스트 결과 */}
              {reconnectChecklist && (
                <div className="reconnect-checklist">
                  {[
                    { key: 'configUpdate', label: '설정 업데이트' },
                    { key: 'sshConnect', label: 'SSH 연결 검증' },
                    { key: 'vllmStart', label: 'vLLM 서버 시작' },
                    { key: 'healthCheck', label: 'vLLM 헬스체크' },
                  ].map(item => {
                    const check = reconnectChecklist[item.key];
                    if (!check) return null;
                    const icon = check.status === 'ok' ? '✅' : check.status === 'fail' ? '❌' : check.status === 'wait' ? '⏳' : '⬜';
                    return (
                      <div key={item.key} className={`checklist-item ${check.status}`}>
                        <span className="checklist-icon">{icon}</span>
                        <span className="checklist-label">{item.label}</span>
                        {check.message && <span className="checklist-msg">{check.message}</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 설정 */}
      <div className="vllm-config-section">
        <h3>⚙️ 서버 설정</h3>
        <div className="config-form">
          <div className="config-field">
            <label>GPU 서버 제공자</label>
            <select
              value={config.provider}
              onChange={(e) => handleConfigChange('provider', e.target.value)}
            >
              <option value="tensordock">TensorDock</option>
              <option value="runpod">RunPod</option>
              <option value="lambda">Lambda</option>
              <option value="vastai">Vast.ai</option>
              <option value="custom">직접 서버</option>
            </select>
          </div>

          <div className="config-field full-width">
            <label>vLLM 엔드포인트 URL</label>
            <input
              className="mono"
              value={config.vllmEndpoint}
              onChange={(e) => handleConfigChange('vllmEndpoint', e.target.value)}
              placeholder={config.provider === 'runpod' ? 'https://xxx-8000.proxy.runpod.net' : 'http://IP:포트'}
            />
            <span className="hint">
              {config.provider === 'runpod' ? 'RunPod proxy URL (예: https://xxx-8000.proxy.runpod.net)'
                : config.provider === 'tensordock' ? 'TensorDock 서버 IP:포워딩포트 (예: http://80.188.223.202:10238)'
                : 'vLLM 서버 주소 (예: http://IP:8000)'}
            </span>
          </div>

          <div className="config-field">
            <label>모델명</label>
            <input
              className="mono"
              value={config.modelName}
              onChange={(e) => handleConfigChange('modelName', e.target.value)}
              placeholder="docuai-ft"
            />
          </div>

          {/* TensorDock 전용 필드 */}
          {config.provider === 'tensordock' && (
            <>
              <div className="config-field">
                <label>SSH 접속 정보</label>
                <input
                  className="mono"
                  value={config.tensorDockIp ? `ssh -p ${config.tensorDockSshPort || '10239'} user@${config.tensorDockIp}` : ''}
                  readOnly
                  placeholder="위 엔드포인트 설정 시 자동 생성"
                  style={{ backgroundColor: '#f8f9fa' }}
                />
                <span className="hint">SSH로 서버에 접속하여 vLLM 관리</span>
              </div>
            </>
          )}

          {/* RunPod 전용 필드 */}
          {config.provider === 'runpod' && (
            <>
              <div className="config-field">
                <label>RunPod API Key</label>
                <input
                  type="password"
                  className="mono"
                  value={config.runpodApiKey}
                  onChange={(e) => handleConfigChange('runpodApiKey', e.target.value)}
                  placeholder="RunPod API 키 입력"
                />
                <span className="hint">RunPod → Settings → API Keys에서 발급</span>
              </div>
              <div className="config-field">
                <label>RunPod Pod ID</label>
                <input
                  className="mono"
                  value={config.runpodPodId}
                  onChange={(e) => handleConfigChange('runpodPodId', e.target.value)}
                  placeholder="6zpavjze4er5oq"
                />
                <span className="hint">RunPod 대시보드 URL에서 확인 가능</span>
              </div>
            </>
          )}

          <div className="config-field full-width">
            <label>HuggingFace 모델 저장소</label>
            <input
              className="mono"
              value={config.hfRepo}
              onChange={(e) => handleConfigChange('hfRepo', e.target.value)}
              placeholder="kimjungmin/docuai-qwen3vl-32b-ft"
            />
            <span className="hint">신규 서버 배포 시 모델 다운로드 출처</span>
          </div>
        </div>

        <div className="config-actions">
          <button
            className="btn-save"
            onClick={handleSaveConfig}
            disabled={!configDirty || saving}
          >
            {saving ? '저장 중...' : '💾 설정 저장'}
          </button>
        </div>
      </div>

      {/* 도움말 */}
      <div className="vllm-help">
        <button className="vllm-help-toggle" onClick={() => setHelpOpen(!helpOpen)}>
          {helpOpen ? '▼' : '▶'} 📖 사용 가이드
        </button>

        {helpOpen && (
          <div className="vllm-help-content">
            {config.provider === 'tensordock' && (
              <>
                <div className="help-step">
                  <h4>1️⃣ TensorDock 서버 배포</h4>
                  <ol>
                    <li>TensorDock에서 A100 80GB 서버 생성</li>
                    <li>SSH로 접속: <code>ssh -p [SSH포트] user@[서버IP]</code></li>
                    <li>배포 스크립트 실행:</li>
                  </ol>
                  <div className="code-block">{`export HF_TOKEN=hf_xxxxx\nbash deploy.sh`}</div>
                  <p>약 <strong>15분</strong> 소요 (pip 설치 + 모델 다운로드 + vLLM 시작)</p>
                </div>

                <div className="help-step">
                  <h4>2️⃣ 포트 포워딩 확인</h4>
                  <p>TensorDock에서 포트 매핑 확인 (대시보드 → Networking):</p>
                  <div className="code-block">{`내부 포트 8000 → 외부 포트 (예: 10238)\n엔드포인트: http://[서버IP]:[외부포트]`}</div>
                </div>

                <div className="help-step">
                  <h4>3️⃣ vLLM 서버 수동 재시작</h4>
                  <p>SSH 접속 후:</p>
                  <div className="code-block">{`bash ~/workspace/start_vllm.sh\ntail -f ~/workspace/vllm_ft.log`}</div>
                </div>
              </>
            )}

            {config.provider === 'runpod' && (
              <>
                <div className="help-step">
                  <h4>1️⃣ RunPod Pod 시작/중지</h4>
                  <ol>
                    <li>위 설정에서 <strong>RunPod API Key</strong>와 <strong>Pod ID</strong>를 입력하고 저장</li>
                    <li><strong>▶ Pod 시작</strong> 버튼 클릭 → Pod이 시작되고 <code>setup.sh</code>가 자동 실행됩니다</li>
                    <li>약 <strong>2분</strong> 후 vLLM 서버가 준비됩니다 (상태 새로고침으로 확인)</li>
                    <li>사용 완료 후 <strong>⏹ Pod 중지</strong> 클릭 → 디스크 비용만 발생</li>
                  </ol>
                </div>

                <div className="help-step">
                  <h4>2️⃣ RunPod API Key 발급</h4>
                  <ol>
                    <li><a href="https://www.runpod.io/console/user/settings" target="_blank" rel="noreferrer">RunPod Settings</a> 접속</li>
                    <li>좌측 메뉴 <strong>"API Keys"</strong> 클릭</li>
                    <li><strong>"Create API Key"</strong> → Read & Write 권한으로 생성</li>
                    <li>생성된 키를 위 설정에 입력</li>
                  </ol>
                </div>
              </>
            )}

            <div className="help-step">
              <h4>📊 현재 모델 정보</h4>
              <p>
                <strong>Base:</strong> Qwen/Qwen3-VL-32B-Instruct<br/>
                <strong>Fine-tuning:</strong> LoRA 머지 모델 (66.7GB, bfloat16)<br/>
                <strong>서빙:</strong> vLLM v0.17+ (max_model_len=16384)<br/>
                <strong>HuggingFace:</strong> <code>{config.hfRepo || 'kimjungmin/docuai-qwen3vl-32b-ft'}</code>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VLLMServerPanel;
